import React from 'react'
import { ActivityIndicator, FlatList, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Text } from '@components'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import type { ScrapedProfile } from '@hooks'
import { ScrapedProfileItem } from './ScrapedProfileItem'

interface ScrapedProfilesListProps {
  profiles: ScrapedProfile[]
  selectedIds: Set<number>
  onToggle: (index: number) => void
  disabledUsernames?: Set<string>
  // The results come 100 at a time: `onLoadMore` asks for the next ones.
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
}

export const ScrapedProfilesList = ({
  profiles,
  selectedIds,
  onToggle,
  disabledUsernames,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}: ScrapedProfilesListProps) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)

  return (
    <FlatList
      data={profiles}
      keyExtractor={(_, index) => index.toString()}
      renderItem={({ item, index }) => {
        const isDisabled = !!(disabledUsernames && item.username && disabledUsernames.has(item.username))
        return (
          <ScrapedProfileItem
            profile={item}
            isSelected={selectedIds.has(index)}
            onToggle={() => onToggle(index)}
            disabled={isDisabled}
          />
        )
      }}
      ItemSeparatorComponent={() => <View style={styles.separator} />}
      ListFooterComponent={
        isLoadingMore ? (
          <ActivityIndicator color={theme.colors.primary} style={styles.loadMore} />
        ) : hasMore && onLoadMore ? (
          <TouchableOpacity onPress={onLoadMore} style={styles.loadMore} testID="social-import-load-more">
            <Text type="body" color={theme.colors.primary}>
              {t('ftSocialImport.results.loadMore')}
            </Text>
          </TouchableOpacity>
        ) : null
      }
      style={styles.list}
      contentContainerStyle={styles.listContent}
    />
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    list: {
      flex: 1,
    },
    listContent: {
      paddingBottom: theme.spacing.lg,
    },
    loadMore: {
      alignItems: 'center',
      paddingVertical: theme.spacing.md,
    },
    separator: {
      height: 1,
      backgroundColor: theme.colors.grey,
      marginHorizontal: theme.spacing.md,
    },
  })
